"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { Loader2, MessageSquare } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import Link from "next/link"

import { postCommentProductById } from "@/core/lib/api/main/main-shop-product"
import {
  CommentFormData,
  getCommentFormSchema,
} from "@/core/schemas/commentSchema"
import { useDictionary } from "@/core/hooks/use-dictionary" // اگر داری
import { cn } from "@/core/lib/utils"
import { CommentItem } from "@/core/types/comments"
import { Heading } from "@/components/ui/Heading"
import CustomButton from "@/themes/tolica/components/CustomButton"
import { useAuth } from "@/contexts/authContext"

interface CommentFormProps {
  productId: number
  comments: CommentItem[]
}

export default function CommentForm({ productId, comments }: CommentFormProps) {
  const [showForm, setShowForm] = useState(false)
  const { dictionary } = useDictionary() // اگر از چندزبانه استفاده نمی‌کنی → حذف کن
  const commentSchema = getCommentFormSchema()
  const {
    state: { isAuthenticated },
  } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    reset,
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
  })

  const onSubmit = async (data: CommentFormData) => {
    if (!isAuthenticated) {
      return;
    }

    try {
      const result = await postCommentProductById(productId, {
        content: data.content,
      });

      if (result.status === 'success') {
        toast.success(result.message);
        reset();
      } else if (result.status === 'error') {
        const contentError = result.data?.errors?.content;

        setError('content', { type: 'server', message: contentError });
        toast.error(result.message);
      } else {
        toast.error(result.message || dictionary.errors.createError);
      }
    } catch (err) {
      console.error(err);
      setError('content', { type: 'server', message: dictionary.common?.error });
    }
  };

  if (!isAuthenticated) {
    return (
      <>
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-1.5 items-center">
            <MessageSquare className="w-4 h-4 stroke-2.5" />

            <Heading level={2}>{dictionary.nav.comments}</Heading>
          </div>
          {comments.length > 0 && <span className="text-xs text-primary-600">
            {comments.length} {comments.length === 1 ? dictionary.nav.comment : dictionary.nav.comments}
          </span>}
        </div>
        <div className="mb-8 flex items-center justify-center flex-col">
          <p className="mb-4">{dictionary.ui?.pages.alertforcomment}</p>
          <CustomButton
            variant="black"
            size="lg"
          >
            <Link href="/login">
              {dictionary.forms?.login} | {dictionary.forms?.signin}
            </Link>
          </CustomButton>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-row  justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex gap-1.5 items-center">
            <MessageSquare className="w-4 h-4 stroke-2.5" />

            <Heading level={2}>{dictionary.nav.comments}</Heading>
          </div>
          {comments.length > 0 && <span className="text-xs text-primary-600">
            {comments.length} {comments.length === 1 ? dictionary.nav.comment : dictionary.nav.comments}
          </span>}

        </div>
        <CustomButton
          size="sm"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? dictionary.common.cancel : dictionary.common.submit + " " + dictionary.nav.comment}
        </CustomButton>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 p-6 bg-primary-100 border border-primary-200 mt-6"
        >
          <div className="flex flex-col gap-2">
            <label
              htmlFor="comment-content"
              className="text-xs uppercase text-muted-foreground"
            >
              {dictionary.ui?.pages.yourComment}
            </label>
            <textarea
              id="comment-content"
              {...register("content")}
              rows={4}
              className={cn(
                "px-4 py-3 bg-background border border-border text-sm text-foreground",
                "placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-foreground",
                "transition-all resize-none rounded-sm",
                errors.content && "border-destructive focus:ring-destructive",
              )}
              placeholder={dictionary.ui?.pages.yourComment}
              disabled={isSubmitting}
            />
            {errors.content && (
              <p className="text-xs text-destructive mt-1">{errors.content.message}</p>
            )}
          </div>

          <CustomButton
            type="submit"
            disabled={isSubmitting}
            size="full"
            variant="black"
          >
            {isSubmitting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <>
                {dictionary.common.send}
              </>
            )}

          </CustomButton>
        </form>
      )}
    </>
  )
}